import { inject, Injectable, OnDestroy } from "@angular/core";
import { AuthCredentials } from "src/app/modules/user";
import { Auth, signInWithEmailAndPassword, sendEmailVerification, UserCredential, createUserWithEmailAndPassword, sendPasswordResetEmail, signInAnonymously, EmailAuthProvider, linkWithCredential, User, authState, getRedirectResult, GoogleAuthProvider, signInWithRedirect, signInWithPopup, OAuthProvider, updatePassword, AuthProvider, linkWithPopup, linkWithRedirect, signInWithCredential} from '@angular/fire/auth';
import { firstValueFrom, map, Subscription } from "rxjs";
import { AdminService } from "./admin.service";
import { NavController } from "@ionic/angular";

/**
 * ID-Token des anonymen Kontos, zwischengelagert fuer den Redirect-Weg.
 *
 * Beim Popup-Weg liegt alles in einem Aufruf vor. Beim Redirect verlaesst die
 * App den Prozess und kommt in einem neuen Dokument zurueck - das Token muss
 * den Wechsel ueberleben, sonst laesst sich der Anspruch auf den anonymen Song
 * danach nicht mehr belegen.
 */
const ANON_TOKEN_STORAGE_KEY = 'melowish_anon_id_token';

/**
 * Ergebnis einer Provider-Anmeldung.
 *
 * Ob anonymer Besitz umzuhaengen ist, steht **nicht** hier drin, sondern in
 * `pendingMergeToken` am Service. Grund: `signInWithCredential` loest sofort
 * `authState` aus, und der AuthFlowService reagiert darauf, noch bevor dieser
 * Aufruf zurueckkehrt. Laege die Information nur im Rueckgabewert, wuerde der
 * Login-Flow anlaufen und moeglicherweise wegnavigieren, bevor der Song beim
 * neuen Konto ist. Ueber das Feld sehen beide Wege dieselbe Aufgabe.
 */
export interface ProviderSignInResult {
  user: User | null;
}

@Injectable({
  providedIn: "root",
})
export class AuthService implements OnDestroy {
  private fireAuth = inject(Auth);
  private adminService = inject(AdminService);
  logoutTriggered = false;

  currentUser: User | null = null;

  readonly authState$ = authState(this.fireAuth);

  readonly userId$ = this.authState$.pipe(
    map(user => user ? user.uid : null)
  );
  readonly isLoggedIn$ = this.authState$.pipe(
    map(user => !!user)
  );

  private sub = this.authState$.subscribe(user => {
    console.log('AuthStateChanged', user);
    this.currentUser = user;
  });

  constructor(
      // public profileService: ProfileService,
      public navCtrl: NavController
  ) {
    if(this.adminService.isNativeMobile()) {
      this.handleRedirectResult();
    }
  }

  ngOnDestroy() {
    // when manually subscribing to an observable remember to unsubscribe in ngOnDestroy
    // Da der Service providedIn: 'root' ist, wird er im Normalfall nie zerstört, ngOnDestroy ist hier eher theoretisch. Das unsubscribe schadet nicht, ist aber in der Praxis kaum relevant, weil die App beim Reload eh komplett entladen wird.
    this.sub.unsubscribe();
  }

  // Funktion zum Zurückgeben eines Promises, das den aktuellen Benutzer zurückgibt
  getUser(): Promise<User | null> {
    return firstValueFrom(this.authState$);
  }

  get userId() {
    return this.currentUser ? this.currentUser.uid : "";
  }

  /**
   * Rueckkehr aus dem Redirect-Weg (nativ/mobil).
   *
   * Zwei Ausgaenge sind hier moeglich, und der zweite ist der Grund, warum die
   * Methode ueberhaupt etwas tut: kam der Nutzer aus einer anonymen Sitzung und
   * gehoert die Identitaet bereits einem Konto, wirft `getRedirectResult` mit
   * `auth/credential-already-in-use`. Dann wird er am bestehenden Konto
   * angemeldet, und `pendingMergeToken` traegt das vorher gesicherte Token, mit
   * dem der AuthFlowService den anonymen Besitz umhaengen laesst.
   */
  private async handleRedirectResult() {
    try {
      const result = await getRedirectResult(this.fireAuth);
      console.log('[RedirectResult]', result);

      if (result?.user) {
        // Verknuepfen hat geklappt - die uid ist dieselbe geblieben, es ist
        // nichts umzuhaengen. Das gesicherte Token wird nicht mehr gebraucht.
        this.takeStoredAnonToken();
        // Navigation kommt ueber authState, hier bewusst keine erzwingen.
      }
    } catch (e: any) {
      const anonIdToken = this.takeStoredAnonToken();
      const merged = await this.signInAfterFailedLink(e, anonIdToken);
      if (merged) return;

      console.error('getRedirectResult failed', e);
    }
  }

  /**
   * Liegt hier ein Token, muss nach der Anmeldung noch anonymer Besitz
   * umgehaengt werden. Wird vom AuthFlowService abgeholt, weil der
   * Redirect-Weg sein Ergebnis nicht an den ausloesenden Aufruf zurueckgeben
   * kann - die App wurde zwischenzeitlich neu geladen.
   */
  pendingMergeToken: string | null = null;

  takePendingMergeToken(): string | null {
    const token = this.pendingMergeToken;
    this.pendingMergeToken = null;
    return token;
  }

  async loginUser(email: string, password: string): Promise<AuthCredentials> {
    const firebaseCredential = await signInWithEmailAndPassword(this.fireAuth, email, password);
    return { uid: firebaseCredential.user.uid };
  }


  async verifyMail(): Promise<void> {
    const user = this.fireAuth.currentUser;
    if (!user) {
      console.error("verifyMail() - currentUser is empty", this.fireAuth);
      return;
    }
    return sendEmailVerification(user);
  }

  signupUser(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.fireAuth, email, password);
  }

  signInAnonUser(): Promise<UserCredential> {
    return signInAnonymously(this.fireAuth);
  }

  /**
   * Macht aus dem anonymen Konto ein echtes - mit E-Mail und Passwort.
   *
   * Gegenstueck zu `signInOrLink` fuer den Passwort-Weg, und aus demselben
   * Grund noetig: `createUserWithEmailAndPassword` wuerde die anonyme Sitzung
   * **ersetzen** statt sie aufzuwerten, und der gerade erzeugte Song waere weg.
   * `linkWithCredential` behaelt die `uid`.
   *
   * Rueckgabe: leerer String bei Erfolg, `CONVERT_MERGED` wenn stattdessen an
   * einem bestehenden Konto angemeldet wurde, sonst der Firebase-Fehlercode.
   */
  async convertAnonUserToPermanent(email: string, password: string): Promise<string> {
    const current = this.fireAuth.currentUser;
    if (!current) {
      return "auth/no-current-user";
    }

    // Vor dem moeglichen Sitzungswechsel holen - danach ist es nicht mehr zu
    // bekommen, und ohne das Token laesst sich der Anspruch auf den anonymen
    // Song nicht belegen.
    const anonIdToken = current.isAnonymous ? await current.getIdToken() : null;
    const credential = EmailAuthProvider.credential(email, password);

    try {
      const userCred = await linkWithCredential(current, credential);
      this.verifyMail();
      console.log("Anonymous account successfully upgraded", userCred.user);
      return "";
    } catch (error: any) {
      const collision =
        error?.code === 'auth/email-already-in-use' ||
        error?.code === 'auth/credential-already-in-use';

      // Es gibt schon ein Konto mit dieser Adresse. Dann wird dort angemeldet
      // und der anonyme Besitz serverseitig umgehaengt - sonst waere der Song
      // im Moment der Conversion verloren.
      if (collision && anonIdToken) {
        try {
          this.pendingMergeToken = anonIdToken;
          await signInWithEmailAndPassword(this.fireAuth, email, password);
          return AuthService.CONVERT_MERGED;
        } catch (signInError: any) {
          // Passwort passt nicht zum bestehenden Konto. Dann bleibt die anonyme
          // Sitzung bestehen, der Song ist nicht verloren.
          this.pendingMergeToken = null;
          console.error("Anmeldung am bestehenden Konto fehlgeschlagen", signInError);
          return signInError?.code ?? error.code;
        }
      }

      console.error("Error upgrading anonymous account", error);
      return error.code;
    }
  }

  /** Signal aus convertAnonUserToPermanent: an bestehendem Konto angemeldet. */
  static readonly CONVERT_MERGED = 'melowish/merged-into-existing';

  /**
   * Setzt das Passwort des angemeldeten Nutzers.
   *
   * Wirft mit code 'auth/requires-recent-login', wenn die Sitzung zu alt ist -
   * Firebase verlangt fuer diese Operation eine frische Anmeldung.
   */
  async updatePassword(newPassword: string): Promise<void> {
    const user = this.fireAuth.currentUser;
    if (!user) {
      throw new Error('updatePassword() - kein angemeldeter Nutzer');
    }
    await updatePassword(user, newPassword);
  }

  async resetPassword(email: string): Promise<void> {
    console.log("send reset", email);
    try {
      const f = await sendPasswordResetEmail(this.fireAuth, email);
      console.log("successfully send pw reset", f);
    } catch (e) {
      console.error("fail send pw reset", e);
    }
  }

  async signInGoogle(): Promise<ProviderSignInResult> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return this.signInOrLink(provider);
  }

  async signInApple(): Promise<ProviderSignInResult> {
    const provider = new OAuthProvider('apple.com');
    return this.signInOrLink(provider);
  }

  /**
   * Meldet mit einem Provider an - und **verknuepft** dabei ein laufendes
   * anonymes Konto, statt es zu ersetzen.
   *
   * Das ist der Kern von 2.1a. `signInWithPopup` auf einer anonymen Sitzung
   * legt einen neuen Nutzer mit neuer `uid` an; der gerade erzeugte Song waere
   * verwaist, und zwar genau im Moment der Conversion. `linkWithPopup` haengt
   * die Identitaet stattdessen an das bestehende anonyme Konto - die `uid`
   * bleibt, der Song bleibt.
   *
   * Drei Wege:
   *  - **nicht anonym** -> normale Anmeldung, nichts Besonderes.
   *  - **anonym, Identitaet frei** -> verknuepfen, `uid` bleibt gleich.
   *  - **anonym, Identitaet gehoert schon einem Konto** -> Firebase wirft
   *    `auth/credential-already-in-use`. Dann wird der Nutzer an seinem
   *    bestehenden Konto angemeldet und der anonyme Besitz muss serverseitig
   *    umgehaengt werden; die Aufgabe liegt danach in `pendingMergeToken`.
   */
  private async signInOrLink(provider: AuthProvider): Promise<ProviderSignInResult> {
    const current = this.fireAuth.currentUser;
    const useRedirect = this.adminService.isNativeMobile();

    if (!current?.isAnonymous) {
      if (useRedirect) {
        await signInWithRedirect(this.fireAuth, provider);
        return { user: null };
      }
      const cred = await signInWithPopup(this.fireAuth, provider);
      return { user: cred.user };
    }

    // Token holen, solange die anonyme Sitzung noch laeuft. Danach ist es nicht
    // mehr zu bekommen, und ohne das Token laesst sich der Anspruch auf den
    // anonymen Song nicht mehr belegen.
    const anonIdToken = await current.getIdToken();

    if (useRedirect) {
      // Der Redirect verlaesst die App - das Token muss den Wechsel
      // ueberleben. Aufgeraeumt wird es in handleRedirectResult().
      this.storeAnonToken(anonIdToken);
      await linkWithRedirect(current, provider);
      return { user: null };
    }

    try {
      const cred = await linkWithPopup(current, provider);
      return { user: cred.user };
    } catch (error: any) {
      const merged = await this.signInAfterFailedLink(error, anonIdToken);
      if (!merged) throw error;
      return { user: merged };
    }
  }

  /**
   * Behandelt den Kollisionsfall nach einem fehlgeschlagenen Verknuepfen.
   *
   * Gibt den angemeldeten Nutzer zurueck, wenn es dieser Fall war - sonst
   * `null`, damit der Aufrufer den urspruenglichen Fehler weiterreicht.
   */
  private async signInAfterFailedLink(
    error: any,
    anonIdToken: string | null
  ): Promise<User | null> {
    if (error?.code !== 'auth/credential-already-in-use') {
      return null;
    }

    // Die Anmeldedaten stecken im Fehler. OAuthProvider deckt Apple und jeden
    // generischen Anbieter ab, GoogleAuthProvider den Google-Fall.
    const credential =
      OAuthProvider.credentialFromError(error) ??
      GoogleAuthProvider.credentialFromError(error);

    if (!credential) {
      console.error('credential-already-in-use ohne Credential im Fehler', error);
      return null;
    }

    // VOR dem Sitzungswechsel hinterlegen: die Anmeldung loest sofort
    // authState aus, und wer immer zuerst reagiert, soll die Aufgabe schon
    // vorfinden - sonst laeuft der Login-Flow an, bevor der Song umgehaengt ist.
    this.pendingMergeToken = anonIdToken;

    const cred = await signInWithCredential(this.fireAuth, credential);
    console.log('Bestehendes Konto angemeldet, anonymer Besitz wird umgehaengt');
    return cred.user;
  }

  private storeAnonToken(token: string): void {
    try {
      sessionStorage.setItem(ANON_TOKEN_STORAGE_KEY, token);
    } catch {
      // Storage blockiert: der Merge-Weg entfaellt, die Anmeldung laeuft weiter.
    }
  }

  /** Einmalig lesen und den Platz raeumen. */
  takeStoredAnonToken(): string | null {
    try {
      const token = sessionStorage.getItem(ANON_TOKEN_STORAGE_KEY);
      if (token) sessionStorage.removeItem(ANON_TOKEN_STORAGE_KEY);
      return token;
    } catch {
      return null;
    }
  }


  logoutUser(): Promise<void> {
    this.logoutTriggered = true;
    return this.fireAuth.signOut();
  }

  async deleteUser(): Promise<void> {
    const user = this.fireAuth.currentUser;
    if (!user) {
      console.error("deleteUser() - currentUser is empty", this.fireAuth);
      return;
    }
    return user.delete();
  }
}
